import { Text } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import { formatInteger } from '../../../../lib/format';
import { Money, OrgCell } from '../../components/cells';
import type { Indexed } from '../../../../lib/types';
import { useT } from '../../../../i18n/I18nProvider';

export interface UsageRow {
  /** `organisation_id:currency`: a tenant billed in two currencies is two rows. */
  id: string;
  organisation_id: string;
  events: number;
  debit_minor: number;
  credit_minor: number;
  currency: string;
}

interface Props {
  records: UsageRow[];
  loading: boolean;
  onRowClick: (row: UsageRow) => void;
}

export function UsageTable({ records, loading, onRowClick }: Props) {
  const t = useT();
  return (
    <DataTable<Indexed<UsageRow>>
      records={records as Indexed<UsageRow>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('billing.usage.emptyRows')}
      onRowClick={(r) => onRowClick(r)}
      columns={[
        {
          accessor: 'organisation_id',
          title: t('billing.usage.columns.organisation'),
          render: (r) => <OrgCell id={r.organisation_id} />
        },
        {
          accessor: 'currency',
          title: t('common.currency'),
          render: (r) => <Text role="bodySmall" c="var(--rr-color-text-muted)">{r.currency}</Text>
        },
        {
          accessor: 'events',
          title: t('billing.usage.columns.events'),
          textAlign: 'right',
          sortable: true,
          render: (r) => formatInteger(r.events)
        },
        {
          accessor: 'debit_minor',
          title: t('billing.usage.columns.totalDebit'),
          textAlign: 'right',
          sortable: true,
          // The rollup stores debits as a positive magnitude and the column
          // heading already says the direction, so the tone is explicit rather
          // than derived from a sign the figure does not carry.
          render: (r) => <Money minor={r.debit_minor} currency={r.currency} tone="debit" />
        },
        {
          accessor: 'credit_minor',
          title: t('billing.usage.columns.totalCredit'),
          textAlign: 'right',
          sortable: true,
          render: (r) => <Money minor={r.credit_minor} currency={r.currency} tone="credit" />
        },
        {
          accessor: 'net_minor',
          title: t('billing.usage.columns.net'),
          textAlign: 'right',
          // Net is what the window did to the wallet: credits in, debits out.
          render: (r) => <Money minor={r.credit_minor - r.debit_minor} currency={r.currency} />
        }
      ]}
    />
  );
}
